/**
 * Central League Registry
 *
 * THE single source of truth for all supported leagues/sports.
 * Add a new sport here and every app picks it up automatically.
 *
 * When adding a new sport:
 *   1. Add the code to SportCode union
 *   2. Add the entry to SUPPORTED_LEAGUES
 */

/** Union of all supported sport codes */
export type SportCode = 'NFL' | 'NBA' | 'NCAAMB' | 'MLB' | 'PGA' | 'SOCCER';

export interface LeagueConfig {
  /** Lowercase league identifier, e.g. 'nfl' */
  id: string;
  /** Full league name, e.g. 'National Football League' */
  name: string;
  /** Uppercase sport code, e.g. 'NFL' */
  code: SportCode;
  /** Path to league icon (for admin list pages) */
  iconSrc?: string;
  /** Label shown in filter dropdowns */
  filterLabel?: string;
  /**
   * Name of the icon component to render in the admin Sports sidebar.
   * Matches a key in `apps/admin/src/assets/Icon.tsx` so consumers can
   * iterate SUPPORTED_LEAGUES instead of hardcoding the list of links.
   */
  sidebarIconName?: string;
  /** Label shown in the admin sidebar (defaults to filterLabel/name). */
  sidebarLabel?: string;
}

export const SUPPORTED_LEAGUES: readonly LeagueConfig[] = [
  { id: 'nba', name: 'National Basketball Association', code: 'NBA', iconSrc: '/nba-logo.svg', filterLabel: 'NBA', sidebarIconName: 'Basketball', sidebarLabel: 'NBA' },
  { id: 'nfl', name: 'National Football League', code: 'NFL', iconSrc: '/league-icon.svg', filterLabel: 'NFL', sidebarIconName: 'Football', sidebarLabel: 'NFL' },
  { id: 'ncaamb', name: "NCAA Men's Basketball", code: 'NCAAMB', iconSrc: '/nba-logo.svg', filterLabel: 'NCAAB', sidebarIconName: 'Basketball', sidebarLabel: 'NCAA Basketball' },
  { id: 'mlb', name: 'Major League Baseball', code: 'MLB', iconSrc: '/league-icon.svg', filterLabel: 'MLB', sidebarIconName: 'Baseball', sidebarLabel: 'MLB' },
  { id: 'pga', name: 'PGA Tour', code: 'PGA', iconSrc: '/pga-logo.svg', filterLabel: 'PGA', sidebarIconName: 'Golf', sidebarLabel: 'PGA' },
  // Soccer is a single sport that internally covers multiple competitions
  // (Premier League, FIFA World Cup, etc.). Feed-service holds the list of
  // active Sportradar competition IDs and merges fixtures across them.
  { id: 'soccer', name: 'Soccer', code: 'SOCCER', iconSrc: '/soccer-logo.svg', filterLabel: 'Soccer', sidebarIconName: 'Soccer', sidebarLabel: 'Soccer' },
];

/** e.g. ['nfl', 'nba', 'ncaamb', 'mlb'] */
export const SUPPORTED_LEAGUE_IDS: readonly string[] = SUPPORTED_LEAGUES.map(l => l.id);

/** e.g. ['NFL', 'NBA', 'NCAAMB', 'MLB'] */
export const SUPPORTED_SPORT_CODES: readonly string[] = SUPPORTED_LEAGUES.map(l => l.code);

/** Map from uppercase sport code to lowercase league ID, e.g. { NFL: 'nfl', NBA: 'nba', ... } */
export const SPORT_TO_LEAGUE_ID: Record<string, string> = Object.fromEntries(
  SUPPORTED_LEAGUES.map(l => [l.code, l.id])
);

/** Formatted list for error messages, e.g. '"nfl", "nba", "ncaamb", or "mlb"' */
export const LEAGUE_ID_LIST: string = SUPPORTED_LEAGUE_IDS.map(id => `"${id}"`).join(', ');

/** Runtime validation helper */
export function isValidLeagueId(id: string): boolean {
  return SUPPORTED_LEAGUE_IDS.includes(id.toLowerCase());
}

/** Sports that are individual (not team-based) */
export const INDIVIDUAL_SPORTS: readonly SportCode[] = ['PGA'];

/** Check if a sport is individual (not team-based) */
export function isIndividualSport(sport: string): boolean {
  return INDIVIDUAL_SPORTS.includes(sport.toUpperCase() as SportCode);
}
